"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, IconButton } from "@/design-system";
import { Icon } from "@/design-system/icons";
import { apiRequest, authenticatedFetch } from "@/utils/api";
import { getErrorMessage } from "@/lib/query";
import { trackActivity } from "@/lib/activityTracking";
import { resolveAgentDisplayName } from "@/lib/agentBranding";
import { useLoopyPageContext } from "@/contexts/LoopyPageContext";
import type {
  SupportConversationResponse,
  SupportMessage,
} from "@/types/support";
import styles from "./SupportChatWidget.module.css";

const POLL_INTERVAL_MS = 15_000;
const LAUNCHER_COLLAPSED_STORAGE_KEY = "survey-loop:support-chat-launcher-collapsed";
const LAUNCHER_COLLAPSED_TTL_MS = 400 * 24 * 60 * 60 * 1000;
const AGENT_DISPLAY_NAME = resolveAgentDisplayName();

function readCachedLauncherCollapsed() {
  if (typeof window === "undefined") return false;

  try {
    const rawValue = window.localStorage.getItem(LAUNCHER_COLLAPSED_STORAGE_KEY);
    if (!rawValue) return false;

    const cachedValue = JSON.parse(rawValue) as { expiresAt?: number };
    if (typeof cachedValue.expiresAt !== "number" || cachedValue.expiresAt <= Date.now()) {
      window.localStorage.removeItem(LAUNCHER_COLLAPSED_STORAGE_KEY);
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

function cacheLauncherCollapsed() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      LAUNCHER_COLLAPSED_STORAGE_KEY,
      JSON.stringify({
        collapsed: true,
        expiresAt: Date.now() + LAUNCHER_COLLAPSED_TTL_MS,
      }),
    );
  } catch {
    // localStorage can be unavailable in private or locked-down browser contexts.
  }
}

function formatTimestamp(value?: string | null) {
  if (!value) return "";

  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type AgentMessage = {
  id: string;
  body: string;
  senderType: "USER" | "AGENT" | "SYSTEM";
  createdAt: string;
  approvalIds?: string[];
  sourceRefs?: Array<{
    id: string;
    title: string;
    type: string;
    url?: string;
  }>;
};

type ChatMessage = SupportMessage | AgentMessage;

type ChatConversation = (NonNullable<SupportConversationResponse["conversation"]> & {
  status?: "OPEN" | "ESCALATED" | "CLOSED";
  escalatedAt?: string | null;
}) | {
  id: string;
  status?: "OPEN" | "ESCALATED" | "CLOSED";
  lastMessageAt?: string | null;
  escalatedAt?: string | null;
};

type AgentApproval = {
  id: string;
  summary: string;
  status?: string;
  payload?: Record<string, unknown>;
  result?: Record<string, unknown> | null;
  error?: string | null;
};

const agentStarterPrompts = [
  "Create a quick survey for weekend activity preferences",
  "Check whether my latest survey is ready to share",
  "Explain what I should look at on this page",
];

function getAgentWorkingMessage(message: string) {
  const normalized = message.toLowerCase();
  if (/\b(create|build|draft|make|new)\b.*\bsurvey\b|\bsurvey\s+for\b/.test(normalized)) {
    return `${AGENT_DISPLAY_NAME} is sketching that survey and checking what needs your approval...`;
  }
  if (/\b(nps|response|responses|analytics|dashboard|metric|trend|report)\b/.test(normalized)) {
    return `${AGENT_DISPLAY_NAME} is checking the workspace data it’s allowed to read...`;
  }
  if (/\b(error|broken|not working|failed|missing|404|login|permission|access)\b/.test(normalized)) {
    return `${AGENT_DISPLAY_NAME} is checking setup and recent workspace context...`;
  }
  return `${AGENT_DISPLAY_NAME} is looking at the relevant Survey Loop context...`;
}

function getAutoNavigationUrl(sourceRefs?: AgentMessage["sourceRefs"]) {
  const destination = sourceRefs?.find((source) => {
    if (source.type !== "surveyloop-data" || !source.url?.startsWith("/")) return false;
    return true;
  });

  return destination?.url || null;
}

function readStringId(value: unknown) {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number") return String(value);
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.$oid === "string" && record.$oid.trim()) return record.$oid.trim();
    if (typeof record.id === "string" && record.id.trim()) return record.id.trim();
  }
  return "";
}

function getApprovalNavigationUrl(approval: AgentApproval) {
  const resultDisplay = getApprovalResultDisplay(approval);
  if (resultDisplay?.afterApprovalRoute) return resultDisplay.afterApprovalRoute;
  const result = approval.result;
  if (!result || typeof result !== "object") return null;
  const survey = result.survey;
  if (!survey || typeof survey !== "object") return null;
  const surveyRecord = survey as Record<string, unknown>;
  const surveyId = readStringId(surveyRecord._id) || readStringId(surveyRecord.id);
  return surveyId ? `/surveys/${surveyId}/edit` : null;
}

function readStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function getApprovalDisplay(approval?: AgentApproval) {
  const display = approval?.payload?.display;
  return display && typeof display === "object" ? display as {
    title?: string;
    targetName?: string;
    targetType?: string;
    changes?: string[];
    riskLevel?: string;
    afterApprovalRoute?: string | null;
  } : null;
}

function getApprovalResultDisplay(approval?: AgentApproval) {
  const display = approval?.result?.display;
  return display && typeof display === "object" ? display as {
    title?: string;
    targetName?: string | null;
    changes?: string[];
    afterApprovalRoute?: string | null;
  } : null;
}

function sortMessagesByCreatedAt(messages: ChatMessage[]) {
  return [...messages].sort((left, right) => (
    new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  ));
}

function dedupeMessages(messages: ChatMessage[]) {
  const seen = new Set<string>();
  return messages.filter((message) => {
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  });
}

function getSenderLabel(message: ChatMessage) {
  if (message.senderType === "CUSTOMER" || message.senderType === "USER") return "You";

  const displayName = "senderDisplayName" in message ? message.senderDisplayName?.trim() : null;
  const isSupportMessage = "receivedFromSlackAt" in message || "deliveredToSlackAt" in message;
  if (isSupportMessage) {
    return displayName && displayName !== "Slack agent" && !/^U[A-Z0-9]+$/i.test(displayName)
      ? displayName
      : "Survey Loop team";
  }

  if (!displayName || displayName === "Slack agent" || /^U[A-Z0-9]+$/i.test(displayName)) {
    return AGENT_DISPLAY_NAME;
  }

  return displayName;
}

function getMessagePreview(message: ChatMessage | undefined) {
  const body = message?.body?.replace(/\s+/g, " ").trim();
  if (!body) return "";
  return body.length > 64 ? `${body.slice(0, 61)}...` : body;
}

interface SupportChatWidgetProps {
  enabled: boolean;
  agentEnabled?: boolean;
}

export function SupportChatWidget({
  enabled,
  agentEnabled = false,
}: SupportChatWidgetProps) {
  const router = useRouter();
  const loopyPageContext = useLoopyPageContext();
  const [authFailed, setAuthFailed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showCapabilities, setShowCapabilities] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [approvalsById, setApprovalsById] = useState<Record<string, AgentApproval>>({});
  const [approvalLoading, setApprovalLoading] = useState<Record<string, boolean>>({});
  const [composerValue, setComposerValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [agentWorkingMessage, setAgentWorkingMessage] = useState<string | null>(null);
  const [lastAgentRequest, setLastAgentRequest] = useState<string | null>(null);
  const [escalating, setEscalating] = useState(false);
  const [isLauncherCollapsed, setIsLauncherCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnreadReply, setHasUnreadReply] = useState(false);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const previousLastMessageIdRef = useRef<string | null>(null);
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      setHasUnreadReply(false);
    }
  }, [isOpen]);

  const mode = agentEnabled ? "agent" : "support";
  const active = (enabled || agentEnabled) && !authFailed;
  const inHumanMode = mode === "agent" && conversation?.status === "ESCALATED";

  useEffect(() => {
    setIsLauncherCollapsed(readCachedLauncherCollapsed());
  }, []);

  const buildClientContext = useCallback(() => {
    return {
      ...loopyPageContext,
      pageTitle: typeof document !== "undefined" ? document.title : "",
      pageMetadata: {
        ...(loopyPageContext.pageMetadata || {}),
      },
    };
  }, [loopyPageContext]);

  useEffect(() => {
    if (enabled || agentEnabled) {
      setAuthFailed(false);
    }
  }, [agentEnabled, enabled]);

  const loadConversation = useCallback(async () => {
    if (!active) return;

    try {
      setError(null);
      let response: { conversation: ChatConversation | null; messages: ChatMessage[] };
      if (mode === "agent") {
        const conversationsResponse = await apiRequest<{ conversations: ChatConversation[] }>(
          "/api/agent/conversations",
        );
        let currentConversation = conversationsResponse.conversations?.[0] || null;
        if (!currentConversation) {
          const created = await apiRequest<{ conversation: ChatConversation }>(
            "/api/agent/conversations",
            { method: "POST", body: JSON.stringify({}) },
          );
          currentConversation = created.conversation;
        }
        response = currentConversation
          ? await apiRequest<{ conversation: ChatConversation; messages: ChatMessage[] }>(
            `/api/agent/conversations/${currentConversation.id}/messages`,
          )
          : { conversation: null, messages: [] };
        const shouldLoadHumanMessages = Boolean(
          response.conversation?.status === "ESCALATED" ||
          response.conversation?.escalatedAt,
        );
        if (shouldLoadHumanMessages) {
          const supportResponse = await apiRequest<SupportConversationResponse>(
            "/api/support-conversations/me",
          ).catch(() => null);
          if (supportResponse?.messages?.length) {
            response = {
              conversation: response.conversation,
              messages: sortMessagesByCreatedAt(dedupeMessages([
                ...(response.messages || []),
                ...supportResponse.messages,
              ])),
            };
          }
        }
      } else {
        response = await apiRequest<SupportConversationResponse>(
          "/api/support-conversations/me",
        );
      }
      const nextMessages = response.messages ?? [];
      const nextLastMessage = nextMessages[nextMessages.length - 1] ?? null;
      const previousLastMessageId = previousLastMessageIdRef.current;

      if (
        nextLastMessage &&
        previousLastMessageId &&
        nextLastMessage.id !== previousLastMessageId &&
        (nextLastMessage.senderType === "AGENT" || nextLastMessage.senderType === "SYSTEM") &&
        !isOpenRef.current
      ) {
        setHasUnreadReply(true);
      }

      previousLastMessageIdRef.current = nextLastMessage?.id ?? null;
      setConversation(response.conversation);
      setMessages(nextMessages);
      if (mode === "agent") {
        const discoveredApprovals = nextMessages.reduce<Record<string, AgentApproval>>((acc, message) => {
          if ("approvalIds" in message && Array.isArray(message.approvalIds)) {
            for (const approvalId of message.approvalIds) {
              acc[String(approvalId)] = {
                ...(acc[String(approvalId)] || {}),
                id: String(approvalId),
                summary: message.body,
              };
            }
          }
          return acc;
        }, {});
        if (Object.keys(discoveredApprovals).length) {
          setApprovalsById((previous) => {
            const next = { ...previous };
            for (const [approvalId, approval] of Object.entries(discoveredApprovals)) {
              next[approvalId] = {
                ...approval,
                ...(previous[approvalId] || {}),
              };
            }
            return next;
          });
        }
      }
    } catch (loadError) {
      if (
        typeof loadError === "object" &&
        loadError !== null &&
        "status" in loadError &&
        (loadError.status === 401 || loadError.status === 403)
      ) {
        setAuthFailed(true);
        return;
      }
      setError(getErrorMessage(loadError, mode === "agent" ? `${AGENT_DISPLAY_NAME} had trouble loading. Try opening it again.` : "Failed to load support chat"));
    } finally {
      setLoading(false);
    }
  }, [active, mode]);

  useEffect(() => {
    if (!active || (mode === "agent" && !isOpen)) return;
    setLoading(true);
    void loadConversation();
  }, [active, isOpen, loadConversation, mode]);

  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(() => {
      void loadConversation();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [active, loadConversation]);

  useEffect(() => {
    if (!active) return;

    const abortController = new AbortController();
    let reconnectTimer: number | undefined;
    let stopped = false;

    const connect = async () => {
      try {
        const response = await authenticatedFetch("/api/support-conversations/me/events", {
          method: "GET",
          headers: { Accept: "text/event-stream" },
          signal: abortController.signal,
        });

        if (response.status === 401 || response.status === 403) {
          setAuthFailed(true);
          return;
        }
        if (response.status === 404) {
          return;
        }

        if (!response.ok || !response.body) {
          throw new Error(`Support event stream failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          let separatorIndex = buffer.indexOf("\n\n");

          while (separatorIndex >= 0) {
            const frame = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);

            if (frame.includes("event: support-conversation-changed")) {
              void loadConversation();
            }

            separatorIndex = buffer.indexOf("\n\n");
          }
        }
      } catch (streamError) {
        if (!stopped && !abortController.signal.aborted) {
          console.warn("Support event stream disconnected:", streamError);
        }
      }

      if (!stopped && !abortController.signal.aborted) {
        reconnectTimer = window.setTimeout(connect, 2_000);
      }
    };

    void connect();

    return () => {
      stopped = true;
      abortController.abort();
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
    };
  }, [active, loadConversation]);

  useEffect(() => {
    if (!isOpen) return;
    const messageList = messageListRef.current;
    if (!messageList) return;
    messageList.scrollTop = messageList.scrollHeight;
  }, [isOpen, messages.length]);

  const handleSourceNavigation = useCallback((url: string, options: { auto?: boolean } = {}) => {
    const targetUrl = url.trim();
    if (!targetUrl) return;

    trackActivity("app.action", {
      action: options.auto ? "agent.source_auto_opened" : "agent.source_opened",
      route: targetUrl,
      conversationId: conversation?.id || null,
    }, options.auto ? `${AGENT_DISPLAY_NAME} opened app destination` : "Opened agent source");
    setIsNavigating(true);

    window.setTimeout(() => {
      try {
        const parsed = new URL(targetUrl, window.location.origin);
        const isSameOrigin = parsed.origin === window.location.origin;
        if (isSameOrigin) {
          router.push(`${parsed.pathname}${parsed.search}${parsed.hash}`);
          setIsOpen(false);
          setIsNavigating(false);
          return;
        }

        window.location.assign(parsed.toString());
      } catch {
        router.push(targetUrl);
        setIsOpen(false);
        setIsNavigating(false);
      }
    }, 140);
  }, [conversation?.id, router]);

  const scheduleAutoNavigation = useCallback((url: string | null) => {
    if (!url) return;
    window.setTimeout(() => handleSourceNavigation(url, { auto: true }), 700);
  }, [handleSourceNavigation]);

  useEffect(() => {
    if (!active || mode !== "agent" || !conversation?.id || inHumanMode) return;

    const abortController = new AbortController();
    let reconnectTimer: number | undefined;
    let stopped = false;

    const connect = async () => {
      try {
        const response = await authenticatedFetch(`/api/agent/conversations/${conversation.id}/events`, {
          method: "GET",
          headers: { Accept: "text/event-stream" },
          signal: abortController.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Agent event stream failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let separatorIndex = buffer.indexOf("\n\n");

          while (separatorIndex >= 0) {
            const frame = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);
            const eventName = frame.match(/^event:\s*(.+)$/m)?.[1]?.trim();
            const dataLine = frame.match(/^data:\s*(.+)$/m)?.[1];
            const data = dataLine ? JSON.parse(dataLine) as { message?: string; payload?: { route?: string | null } | null } : null;
            if (eventName?.startsWith("agent.") && data?.message) {
              setAgentWorkingMessage(data.message);
            }
            if (eventName === "agent.navigation" && data?.payload?.route) {
              scheduleAutoNavigation(data.payload.route);
            }
            if (eventName === "agent.action_completed" || eventName === "agent.action_failed") {
              void loadConversation();
            }
            separatorIndex = buffer.indexOf("\n\n");
          }
        }
      } catch (streamError) {
        if (!stopped && !abortController.signal.aborted) {
          console.warn("Agent event stream disconnected:", streamError);
        }
      }

      if (!stopped && !abortController.signal.aborted) {
        reconnectTimer = window.setTimeout(connect, 2_000);
      }
    };

    void connect();

    return () => {
      stopped = true;
      abortController.abort();
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
    };
  }, [active, conversation?.id, inHumanMode, loadConversation, mode, scheduleAutoNavigation]);

  const handleSend = useCallback(async () => {
    const body = composerValue.trim();
    if (!body || sending) return;
    const sendingToHuman = mode === "support" || inHumanMode;

    setSending(true);
    setAgentWorkingMessage(mode === "agent" && !inHumanMode ? getAgentWorkingMessage(body) : null);
    setError(null);
    if (mode === "agent" && !inHumanMode) setLastAgentRequest(body);
    let optimisticMessageId: string | null = null;

    try {
      trackActivity(sendingToHuman ? "support.message_sent" : "agent.message_sent", {
        action: sendingToHuman ? "support.message_sent" : "agent.message_sent",
        conversationId: conversation?.id || null,
      }, sendingToHuman ? "Sent human handoff message" : `Sent ${AGENT_DISPLAY_NAME} message`);
      if (mode === "agent" && !inHumanMode) {
        const optimisticUserMessage: AgentMessage = {
          id: `pending-user-${Date.now()}`,
          body,
          senderType: "USER",
          createdAt: new Date().toISOString(),
        };
        optimisticMessageId = optimisticUserMessage.id;
        setMessages((previous) => [...previous, optimisticUserMessage]);
        setComposerValue("");

        let currentConversation = conversation;
        if (!currentConversation) {
          const created = await apiRequest<{ conversation: ChatConversation }>(
            "/api/agent/conversations",
            { method: "POST", body: JSON.stringify({}) },
          );
          currentConversation = created.conversation;
          setConversation(currentConversation);
        }
        if (!currentConversation) {
          throw new Error("Agent conversation was not created");
        }
        const abortController = new AbortController();
        activeRequestRef.current = abortController;
        const response = await apiRequest<{
          conversation: ChatConversation;
          userMessage: AgentMessage;
          agentMessage: AgentMessage;
          approvals?: AgentApproval[];
        }>(`/api/agent/conversations/${currentConversation.id}/messages`, {
          method: "POST",
          body: JSON.stringify({
            message: body,
            clientContext: buildClientContext(),
          }),
          signal: abortController.signal,
        });
        setConversation(response.conversation);
        setMessages((previous) => [
          ...previous.filter((message) => message.id !== optimisticUserMessage.id),
          response.userMessage,
          response.agentMessage,
        ]);
        if (response.approvals?.length) {
          setApprovalsById((previous) => ({
            ...previous,
            ...Object.fromEntries(response.approvals?.map((approval) => [approval.id, approval]) || []),
          }));
        }
        previousLastMessageIdRef.current = response.agentMessage.id;
        scheduleAutoNavigation(getAutoNavigationUrl(response.agentMessage.sourceRefs));
      } else {
        const response = await apiRequest<{
          conversation: SupportConversationResponse["conversation"];
          message: SupportMessage;
        }>("/api/support-conversations/me/messages", {
          method: "POST",
          body: JSON.stringify({ body }),
        });

        if (mode === "support") {
          setConversation(response.conversation);
        }
        setMessages((previous) => [...previous, response.message]);
        previousLastMessageIdRef.current = response.message.id;
        setComposerValue("");
      }
    } catch (sendError) {
      if (optimisticMessageId) {
        setMessages((previous) => previous.filter((message) => message.id !== optimisticMessageId));
      }
      setError(
        sendError instanceof DOMException && sendError.name === "AbortError"
          ? `${AGENT_DISPLAY_NAME} stopped working on that request.`
          : getErrorMessage(sendError, mode === "agent" ? `${AGENT_DISPLAY_NAME} couldn't send that. Try once more.` : "Failed to send support message"),
      );
    } finally {
      setSending(false);
      setAgentWorkingMessage(null);
      activeRequestRef.current = null;
    }
  }, [buildClientContext, composerValue, conversation, inHumanMode, mode, scheduleAutoNavigation, sending]);

  const handleCancelAgentRequest = useCallback(() => {
    activeRequestRef.current?.abort();
    setSending(false);
    setAgentWorkingMessage(null);
    trackActivity("app.action", {
      action: "agent.cancelled",
      conversationId: conversation?.id || null,
    }, `Cancelled ${AGENT_DISPLAY_NAME} request`);
  }, [conversation?.id]);

  const handleRetryLastAgentRequest = useCallback(() => {
    if (!lastAgentRequest || sending) return;
    setComposerValue(lastAgentRequest);
    trackActivity("app.action", {
      action: "agent.retry_requested",
      conversationId: conversation?.id || null,
    }, `Retried ${AGENT_DISPLAY_NAME} request`);
  }, [conversation?.id, lastAgentRequest, sending]);

  const handleApprovalAction = useCallback(async (approvalId: string, action: "confirm" | "reject") => {
    if (approvalLoading[approvalId]) return;
    setApprovalLoading((previous) => ({ ...previous, [approvalId]: true }));
    setError(null);

    try {
      trackActivity(
        action === "confirm" ? "agent.approval_confirmed" : "agent.approval_rejected",
        {
          action: action === "confirm" ? "agent.approval_confirmed" : "agent.approval_rejected",
          approvalId,
          conversationId: conversation?.id || null,
        },
        action === "confirm" ? "Confirmed agent approval" : "Rejected agent approval",
      );
      const response = await apiRequest<{ approval: AgentApproval }>(
        `/api/agent/approvals/${approvalId}/${action}`,
        { method: "POST", body: JSON.stringify({}) },
      );
      setApprovalsById((previous) => ({
        ...previous,
        [approvalId]: {
          ...(previous[approvalId] || { id: approvalId, summary: "" }),
          ...response.approval,
        },
      }));
      void loadConversation();
      if (action === "confirm") {
        scheduleAutoNavigation(getApprovalNavigationUrl(response.approval));
      }
    } catch (approvalError) {
      setError(getErrorMessage(approvalError, `Failed to ${action} approval`));
    } finally {
      setApprovalLoading((previous) => ({ ...previous, [approvalId]: false }));
    }
  }, [approvalLoading, conversation?.id, loadConversation, scheduleAutoNavigation]);

  const handleEscalate = useCallback(async () => {
    if (mode !== "agent" || escalating) return;
    setEscalating(true);
    setError(null);

    try {
      let currentConversation = conversation;
      if (!currentConversation) {
        const created = await apiRequest<{ conversation: ChatConversation }>(
          "/api/agent/conversations",
          { method: "POST", body: JSON.stringify({}) },
        );
        currentConversation = created.conversation;
        setConversation(currentConversation);
      }
      if (!currentConversation) {
        throw new Error("Agent conversation was not created");
      }

      trackActivity("agent.support_escalated", {
        action: "agent.support_escalated",
        conversationId: currentConversation.id,
      }, "Requested human handoff");
      const response = await apiRequest<{
        conversation?: ChatConversation;
        supportConversation?: SupportConversationResponse["conversation"];
        message?: string;
      }>(`/api/agent/conversations/${currentConversation.id}/escalate`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (response.conversation) {
        setConversation(response.conversation);
      } else {
        setConversation({
          ...currentConversation,
          status: "ESCALATED",
          escalatedAt: new Date().toISOString(),
        });
      }
      void loadConversation();
    } catch (escalateError) {
      setError(getErrorMessage(escalateError, "Failed to contact a human"));
    } finally {
      setEscalating(false);
    }
  }, [conversation, escalating, loadConversation, mode]);

  const handleResumeAgent = useCallback(async () => {
    if (mode !== "agent" || !conversation?.id) return;
    setError(null);

    try {
      const response = await apiRequest<{ conversation: ChatConversation }>(
        `/api/agent/conversations/${conversation.id}/resume`,
        { method: "POST", body: JSON.stringify({}) },
      );
      setConversation(response.conversation);
      void loadConversation();
      trackActivity("app.action", {
        action: "agent.handoff_resumed",
        conversationId: conversation.id,
      }, `Returned to ${AGENT_DISPLAY_NAME}`);
    } catch (resumeError) {
      setError(getErrorMessage(resumeError, `${AGENT_DISPLAY_NAME} couldn't resume yet. Try once more.`));
    }
  }, [conversation?.id, loadConversation, mode]);

  const handleCollapseLauncher = useCallback(() => {
    setIsLauncherCollapsed(true);
    cacheLauncherCollapsed();
    trackActivity("support.chat_launcher_collapsed", {
      action: "support.chat_launcher_collapsed",
      conversationId: conversation?.id || null,
    }, mode === "agent" ? `Collapsed Ask ${AGENT_DISPLAY_NAME}` : "Collapsed support chat");
  }, [conversation?.id, mode]);

  if (!active) return null;

  return (
    <section className={styles.root} aria-label={mode === "agent" ? `${AGENT_DISPLAY_NAME}, your Survey Loop assistant` : "Support chat"}>
      {isOpen ? (
        <div className={`${styles.panel} ${isNavigating ? styles.panelNavigating : ""}`}>
          <header className={styles.header}>
            <div className={styles.titleGroup}>
              <div className={styles.iconBadge}>
                <Icon name="MessageSquare" size={18} />
              </div>
              <div>
                <h2 className={styles.title}>{mode === "agent" ? AGENT_DISPLAY_NAME : "Support"}</h2>
                <p className={styles.subtitle}>
                  {conversation?.lastMessageAt
                    ? `Updated ${formatTimestamp(conversation.lastMessageAt)}`
                    : mode === "agent" ? "Your always-on Survey Loop assistant" : "Survey Loop support"}
                </p>
              </div>
            </div>
            <div className={styles.headerActions}>
              {mode === "agent" && !inHumanMode ? (
                <IconButton
                  variant="transparent"
                  size="sm"
                  aria-label={`What ${AGENT_DISPLAY_NAME} can do`}
                  leadingVisual="Sparkles"
                  onClick={() => setShowCapabilities((value) => !value)}
                />
              ) : null}
              <IconButton
                variant="transparent"
                size="sm"
                aria-label={mode === "agent" ? `Close ${AGENT_DISPLAY_NAME}` : "Close support chat"}
                leadingVisual="X"
                onClick={() => {
                  trackActivity("support.chat_closed", {
                    action: "support.chat_closed",
                    conversationId: conversation?.id || null,
                  }, mode === "agent" ? `Closed ${AGENT_DISPLAY_NAME}` : "Closed support chat");
                  setIsOpen(false);
                }}
              />
            </div>
          </header>

          {showCapabilities && mode === "agent" && !inHumanMode ? (
            <div className={styles.capabilityMenu} aria-label={`What ${AGENT_DISPLAY_NAME} can do`}>
              {[
                "Explain this page",
                "Check whether this survey is ready",
                "Open my best survey",
                "Show responses",
                "Create a draft survey",
                "Bring in a human",
              ].map((capability) => (
                <button
                  key={capability}
                  type="button"
                  onClick={() => {
                    setComposerValue(capability);
                    setShowCapabilities(false);
                  }}
                >
                  {capability}
                </button>
              ))}
            </div>
          ) : null}

          {inHumanMode ? (
            <div className={styles.handoffBanner} role="status">
              <div>
                <strong>I sent this to the Survey Loop team.</strong>
                <span>You’re talking with a human now. {AGENT_DISPLAY_NAME} is paused until you come back.</span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleResumeAgent()}
              >
                Back to {AGENT_DISPLAY_NAME}
              </Button>
            </div>
          ) : null}

          <div ref={messageListRef} className={styles.messages} aria-live="polite">
            {loading && messages.length === 0 ? (
              <div className={styles.status}>{mode === "agent" ? `Loading ${AGENT_DISPLAY_NAME}...` : "Loading support chat..."}</div>
            ) : null}

            {!loading && messages.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <Icon name="MessagesSquare" size={22} />
                </div>
                <p>{mode === "agent" ? "I can help you build surveys, understand results, check setup, and bring in a human when you need one." : "Send us a note and we will reply here."}</p>
                {mode === "agent" && !inHumanMode ? (
                  <div className={styles.starterPrompts} aria-label="Starter prompts">
                    {agentStarterPrompts.map((prompt, promptIndex) => (
                      <button
                        key={prompt}
                        type="button"
                        className={styles.starterPrompt}
                        onClick={() => {
                          trackActivity("app.action", {
                            action: "agent.starter_prompt_selected",
                            starterPromptIndex: promptIndex,
                            conversationId: conversation?.id || null,
                          }, `Selected ${AGENT_DISPLAY_NAME} starter prompt`);
                          setComposerValue(prompt);
                        }}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {messages.map((message) => {
              const isCustomer = message.senderType === "CUSTOMER" || message.senderType === "USER";
              const approvalIds = mode === "agent" && "approvalIds" in message && Array.isArray(message.approvalIds)
                ? message.approvalIds.map(String)
                : [];
              return (
                <article
                  key={message.id}
                  className={`${styles.messageRow} ${
                    isCustomer ? styles.messageRowCustomer : styles.messageRowAgent
                  }`}
                >
                  {!isCustomer ? <div className={styles.avatar}>S</div> : null}
                  <div
                    className={`${styles.bubble} ${
                      isCustomer ? styles.customerBubble : styles.agentBubble
                    }`}
                  >
                    <div className={styles.messageMeta}>
                      <span>{getSenderLabel(message)}</span>
                      <span>{formatTimestamp(message.createdAt)}</span>
                    </div>
                    <div className={styles.messageBody}>{message.body}</div>
                    {!inHumanMode && approvalIds.length > 0 && (
                      <div className={styles.approvals}>
                        {approvalIds.map((approvalId) => {
                          const approval = approvalsById[approvalId];
                          const status = approval?.status || "PENDING";
                          const disabled = status !== "PENDING" || Boolean(approvalLoading[approvalId]);
                          const display = getApprovalDisplay(approval);
                          const resultDisplay = getApprovalResultDisplay(approval);
                          const changes = readStringArray(resultDisplay?.changes || display?.changes);
                          return (
                            <div key={approvalId} className={styles.approvalCard}>
                              <div className={styles.approvalHeader}>
                                <div>
                                  <strong>{resultDisplay?.title || display?.title || "Approval needed"}</strong>
                                  <span>
                                    {[display?.targetType, resultDisplay?.targetName || display?.targetName]
                                      .filter(Boolean)
                                      .join(" · ") || `Review before ${AGENT_DISPLAY_NAME} changes anything`}
                                  </span>
                                </div>
                                <div className={styles.approvalStatus}>{status}</div>
                              </div>
                              {changes.length > 0 ? (
                                <ul className={styles.approvalChanges}>
                                  {changes.map((change) => (
                                    <li key={change}>{change}</li>
                                  ))}
                                </ul>
                              ) : null}
                              {status === "EXPIRED" ? (
                                <p className={styles.approvalHint}>This approval expired. Ask {AGENT_DISPLAY_NAME} to prepare it again.</p>
                              ) : approval?.error ? (
                                <p className={styles.approvalHint}>{approval.error}</p>
                              ) : status === "CONFIRMED" && (resultDisplay?.afterApprovalRoute || display?.afterApprovalRoute) ? (
                                <p className={styles.approvalHint}>{AGENT_DISPLAY_NAME} will open the updated page automatically.</p>
                              ) : null}
                              <div className={styles.approvalActions}>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => void handleApprovalAction(approvalId, "confirm")}
                                  disabled={disabled}
                                >
                                  Looks good
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => void handleApprovalAction(approvalId, "reject")}
                                  disabled={disabled}
                                >
                                  Not now
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}

            {mode === "agent" && !inHumanMode && sending && agentWorkingMessage ? (
              <article className={`${styles.messageRow} ${styles.messageRowAgent}`}>
                <div className={styles.avatar}>S</div>
                <div className={`${styles.bubble} ${styles.agentBubble} ${styles.workingBubble}`}>
                  <div className={styles.messageMeta}>
                    <span>{AGENT_DISPLAY_NAME}</span>
                    <span>Working</span>
                  </div>
                  <div className={styles.workingLine}>
                    <span className={styles.typingDots} aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    <span>{agentWorkingMessage}</span>
                  </div>
                  <div className={styles.inlineActions}>
                    <Button variant="secondary" size="sm" onClick={handleCancelAgentRequest}>
                      Stop
                    </Button>
                  </div>
                </div>
              </article>
            ) : null}
          </div>

          {error ? (
            <div className={styles.error} role="alert">
              <span>{error}</span>
              {mode === "agent" && lastAgentRequest ? (
                <button type="button" onClick={handleRetryLastAgentRequest}>
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}

          <footer className={styles.composer}>
            <textarea
              className={styles.textarea}
              value={composerValue}
              onChange={(event) => setComposerValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  setIsOpen(false);
                  return;
                }
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
              placeholder={inHumanMode ? "Message the Survey Loop team..." : mode === "agent" ? `Tell ${AGENT_DISPLAY_NAME} what you’re trying to do...` : "Write a reply..."}
              aria-label={inHumanMode ? "Message the Survey Loop team" : mode === "agent" ? `Message ${AGENT_DISPLAY_NAME}` : "Support message"}
              disabled={sending}
              rows={4}
            />
            <div className={styles.composerActions}>
              {mode === "agent" ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => inHumanMode ? void handleResumeAgent() : void handleEscalate()}
                  disabled={escalating}
                >
                  {inHumanMode ? `Back to ${AGENT_DISPLAY_NAME}` : escalating ? "Bringing them in" : "Bring in a human"}
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                leadingVisual="Send"
                onClick={() => void handleSend()}
                disabled={sending || composerValue.trim().length === 0}
              >
                Send
              </Button>
            </div>
          </footer>
        </div>
      ) : (
        <div className={`${styles.launcherWrap} ${isLauncherCollapsed ? styles.launcherWrapCollapsed : ""}`}>
          <button
            type="button"
            className={`${styles.launcher} ${isLauncherCollapsed ? styles.launcherCollapsed : ""}`}
            onClick={() => {
              trackActivity("support.chat_opened", {
                action: "support.chat_opened",
                conversationId: conversation?.id || null,
              }, mode === "agent" ? `Opened ${AGENT_DISPLAY_NAME}` : "Opened support chat");
              setIsOpen(true);
            }}
            aria-label={mode === "agent" ? `Open Ask ${AGENT_DISPLAY_NAME}` : "Open support chat"}
          >
            <Icon name="MessageSquare" size={22} />
            {!isLauncherCollapsed ? (
              <span className={styles.launcherText}>
                <strong>{mode === "agent" ? `Ask ${AGENT_DISPLAY_NAME}` : "Support"}</strong>
                {getMessagePreview(messages[messages.length - 1]) ? (
                  <small>{getMessagePreview(messages[messages.length - 1])}</small>
                ) : null}
              </span>
            ) : null}
            {hasUnreadReply ? <span className={styles.unreadDot} aria-label="Unread reply" /> : null}
          </button>
          {!isLauncherCollapsed ? (
            <button
              type="button"
              className={styles.launcherCollapseButton}
              onClick={handleCollapseLauncher}
              aria-label={mode === "agent" ? `Collapse Ask ${AGENT_DISPLAY_NAME} launcher` : "Collapse support chat launcher"}
            >
              <Icon name="X" size={12} />
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}
